// Shared upload helpers for site surveys. Used by the survey page for live saves
// and by the offline queue to upload changes saved while there was no signal.
import { apiRequest } from '@/lib/queryClient';
import {
  splitSiteSurveyQueuedPhotos,
  transformFormDataForApi,
  type QueuedSiteSurveyPhoto,
} from '@/lib/transformers/site-survey';

export const SITE_SURVEY_QUEUED_PHOTO_UPLOAD_TIMEOUT_MS = 60_000;
export const SITE_SURVEY_QUEUED_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export function cloneSurveyData<T>(data: T): T {
  return JSON.parse(JSON.stringify(data || {}));
}

function setValueAtPath(target: Record<string, any>, fieldPath: string, value: string) {
  const parts = fieldPath.split('.');
  let current: any = target;

  parts.forEach((part, index) => {
    const isLast = index === parts.length - 1;
    const nextPart = parts[index + 1];
    const nextShouldBeArray = nextPart !== undefined && /^\d+$/.test(nextPart);

    if (isLast) {
      current[part] = value;
      return;
    }

    if (current[part] === undefined || current[part] === null) {
      current[part] = nextShouldBeArray ? [] : {};
    }

    current = current[part];
  });
}

function dataUrlExtension(dataUrl: string): string {
  const match = dataUrl.match(/^data:([^;]+);base64,/i);
  const mimeType = match?.[1]?.toLowerCase();

  switch (mimeType) {
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    default:
      return 'jpg';
  }
}

function dataUrlByteLength(dataUrl: string): number {
  const base64 = dataUrl.split(',')[1] || '';
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64 = ''] = dataUrl.split(',');
  const mimeType = header.match(/^data:([^;]+);base64$/i)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mimeType });
}

function queuedPhotoFilename(photo: QueuedSiteSurveyPhoto): string {
  const safeField = photo.fieldPath.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '');
  return `${safeField || 'site-survey-photo'}-${Date.now()}.${dataUrlExtension(photo.dataUrl)}`;
}

async function readUploadError(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) return response.statusText || 'Photo upload failed';

  try {
    const parsed = JSON.parse(text);
    return parsed.error || parsed.message || text;
  } catch {
    return text;
  }
}

/**
 * Upload every photo still held inline (as a data URL) in the survey data and
 * swap each one for its server URL. Photos that fail stay inline so a later
 * attempt can retry them.
 */
export async function uploadQueuedSurveyPhotos({
  surveyId,
  data,
  customerId,
  installationId,
}: {
  surveyId: number;
  data: Record<string, any>;
  customerId?: number | null;
  installationId?: number | null;
}): Promise<{
  data: Record<string, any>;
  uploadedCount: number;
  failures: string[];
  /** Failures that retrying won't fix: photo too large, or rejected by the office. */
  permanentFailureCount: number;
}> {
  const { queuedPhotos } = splitSiteSurveyQueuedPhotos(data);

  if (queuedPhotos.length === 0) {
    return { data, uploadedCount: 0, failures: [], permanentFailureCount: 0 };
  }

  const updatedData = cloneSurveyData(data);
  let uploadedCount = 0;
  let permanentFailureCount = 0;
  const failures: string[] = [];

  for (const photo of queuedPhotos) {
    try {
      if (dataUrlByteLength(photo.dataUrl) > SITE_SURVEY_QUEUED_PHOTO_MAX_BYTES) {
        failures.push(`${photo.fieldPath}: File size exceeds 10MB limit`);
        permanentFailureCount += 1;
        continue;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort('Queued site survey photo upload timed out');
      }, SITE_SURVEY_QUEUED_PHOTO_UPLOAD_TIMEOUT_MS);

      let response: Response;
      try {
        const filename = queuedPhotoFilename(photo);
        const uploadPayload = new FormData();
        uploadPayload.append('image', dataUrlToFile(photo.dataUrl, filename), filename);
        uploadPayload.append('filename', filename);
        uploadPayload.append('siteSurveyId', String(surveyId));
        uploadPayload.append('photoType', photo.fieldPath);
        uploadPayload.append('description', `Site survey ${photo.fieldPath} photo`);
        if (installationId !== undefined && installationId !== null) {
          uploadPayload.append('installationId', String(installationId));
        }
        if (customerId !== undefined && customerId !== null) {
          uploadPayload.append('customerId', String(customerId));
        }

        response = await fetch('/api/site-surveys/photos/upload-photo', {
          method: 'POST',
          credentials: 'include',
          signal: controller.signal,
          body: uploadPayload,
        });
      } catch (error) {
        if (controller.signal.aborted) {
          throw new Error('Photo upload timed out. Please try again.');
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        failures.push(`${photo.fieldPath}: ${await readUploadError(response)}`);
        const temporary =
          response.status >= 500 || [401, 403, 408, 429].includes(response.status);
        if (!temporary) permanentFailureCount += 1;
        continue;
      }

      const result = await response.json();
      if (!result?.success || !result?.url) {
        failures.push(`${photo.fieldPath}: Invalid upload response`);
        continue;
      }

      setValueAtPath(updatedData, photo.fieldPath, result.url);
      uploadedCount += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown upload error';
      failures.push(`${photo.fieldPath}: ${message}`);
    }
  }

  return { data: updatedData, uploadedCount, failures, permanentFailureCount };
}

export interface ServerSurveyVersion {
  id: number;
  updatedAt: string | null;
  status: string | null;
}

/** The office copy of a customer's survey, or null when none exists yet. */
export async function fetchServerSurveyVersion(
  customerId: number
): Promise<ServerSurveyVersion | null> {
  const response = await apiRequest<any>({
    method: 'GET',
    url: `/api/site-surveys/customer/${customerId}?optional=true`,
  });
  const survey = response?.survey ?? null;
  if (!survey?.id) return null;
  return {
    id: survey.id,
    updatedAt: survey.updatedAt ? new Date(survey.updatedAt).toISOString() : null,
    status: survey.status ?? null,
  };
}

export interface QueuedSurveyUploadInput {
  customerId: number;
  installationId?: number;
  surveyId?: number;
  data: Record<string, any>;
}

export interface QueuedSurveyUploadResult {
  surveyId: number;
  serverUpdatedAt: string | null;
  /** Survey data with uploaded photos swapped for their server URLs. */
  data: Record<string, any>;
  failedPhotoCount: number;
  permanentPhotoFailureCount: number;
}

/**
 * Photos reached the office but the save recording their URLs did not. Carries
 * the URL-swapped data so a retry doesn't upload the same photos again.
 */
export class PhotoUrlsNotSavedError extends Error {
  constructor(
    readonly original: unknown,
    readonly surveyId: number,
    readonly data: Record<string, any>
  ) {
    super(original instanceof Error ? original.message : String(original));
    this.name = 'PhotoUrlsNotSavedError';
  }
}

/**
 * Send a survey saved on this device to the office: save the fields, upload any
 * photos taken offline, then save again so the photo URLs are recorded.
 */
export async function uploadQueuedSurvey(
  input: QueuedSurveyUploadInput
): Promise<QueuedSurveyUploadResult> {
  const payload: Record<string, any> = {
    ...input.data,
    customerId: input.customerId,
    installationId: input.installationId,
    ...(input.surveyId ? { id: input.surveyId } : {}),
  };

  let response = await apiRequest<any>({
    url: '/api/site-surveys/save',
    method: 'POST',
    data: transformFormDataForApi(payload),
    headers: { 'X-Survey-Operation': 'offline-upload' },
  });

  const surveyId: number | undefined = response?.surveyId || response?.survey?.id;
  if (!surveyId) {
    throw new Error('The office did not confirm the survey was saved.');
  }

  const photoResult = await uploadQueuedSurveyPhotos({
    surveyId,
    data: payload,
    customerId: input.customerId,
    installationId: input.installationId,
  });

  if (photoResult.uploadedCount > 0) {
    try {
      response = await apiRequest<any>({
        url: '/api/site-surveys/save',
        method: 'POST',
        data: transformFormDataForApi({ ...photoResult.data, id: surveyId }),
        headers: { 'X-Survey-Operation': 'offline-upload-photo-finalize' },
      });
    } catch (error) {
      throw new PhotoUrlsNotSavedError(error, surveyId, photoResult.data);
    }
  }

  const updatedAt = response?.survey?.updatedAt;
  return {
    surveyId,
    serverUpdatedAt: updatedAt ? new Date(updatedAt).toISOString() : null,
    data: photoResult.data,
    failedPhotoCount: photoResult.failures.length,
    permanentPhotoFailureCount: photoResult.permanentFailureCount,
  };
}
